const DISCOUNT_TRIGGER = '[data-fg-discount-code]';
const COPY_TRIGGER = '[data-fg-copy-code]';
const COUNTDOWN_SELECTOR = '[data-fg-offer-countdown][data-countdown-end]';

function normalizeDiscountCode(value) {
  return String(value || '').trim().toLowerCase();
}

function cartContainsDiscount(cart, code) {
  const targetCode = normalizeDiscountCode(code);
  const titles = [];

  (cart.cart_level_discount_applications || []).forEach((application) => {
    titles.push(application.title);
  });

  (cart.discount_applications || []).forEach((application) => {
    titles.push(application.title);
  });

  (cart.items || []).forEach((item) => {
    (item.discount_allocations || item.line_level_discount_allocations || []).forEach((allocation) => {
      titles.push(allocation.discount_application?.title);
    });
  });

  return titles.some((title) => normalizeDiscountCode(title) === targetCode);
}

function offerStatus(trigger) {
  const offer = trigger.closest('.fg-cart-offer, .fg-product-offer');
  return offer?.querySelector('.fg-cart-offer__status, .fg-product-offer__status');
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.append(input);
  input.select();

  const copied = document.execCommand('copy');
  input.remove();

  if (!copied) {
    throw new Error('Copy failed');
  }
}

async function copyOfferCode(trigger) {
  const code = trigger.dataset.fgCopyCode?.trim();

  if (!code || trigger.getAttribute('aria-busy') === 'true') {
    return;
  }

  const originalText = trigger.textContent;
  const status = offerStatus(trigger);
  trigger.setAttribute('aria-busy', 'true');

  try {
    await copyText(code);
    trigger.textContent = 'Copied';
    if (status) status.textContent = `Code ${code} copied.`;
  } catch (_error) {
    trigger.textContent = 'Copy again';
    if (status) status.textContent = `Copy failed. Enter ${code} at checkout.`;
  } finally {
    trigger.removeAttribute('aria-busy');
    window.setTimeout(() => {
      if (trigger.isConnected) trigger.textContent = originalText;
    }, 1600);
  }
}

async function applyCartDiscount(trigger) {
  const code = trigger.dataset.fgDiscountCode?.trim();
  const status = offerStatus(trigger);

  if (!code || trigger.getAttribute('aria-disabled') === 'true' || trigger.getAttribute('aria-busy') === 'true') {
    return;
  }

  trigger.setAttribute('aria-busy', 'true');
  trigger.textContent = 'Applying…';
  status?.classList.remove('is-error');
  if (status) status.textContent = '';

  try {
    const sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );

    const uniqueSections = [...new Set(sections)];
    const body = { discount: code };

    if (uniqueSections.length > 0) {
      body.sections = uniqueSections;
      body.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    const cart = await response.json();

    if (!response.ok) {
      throw new Error(cart.description || 'This discount could not be applied.');
    }

    if (!cartContainsDiscount(cart, code)) {
      throw new Error('This code is not eligible for the current cart.');
    }

    document.querySelectorAll(DISCOUNT_TRIGGER).forEach((candidate) => {
      if (normalizeDiscountCode(candidate.dataset.fgDiscountCode) !== normalizeDiscountCode(code)) return;

      candidate.textContent = 'Applied';
      candidate.classList.add('is-applied');
      candidate.setAttribute('aria-disabled', 'true');
      candidate.setAttribute('tabindex', '-1');
    });

    if (status) status.textContent = 'Applied. Estimated total updated.';

    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.setTimeout(() => window.location.reload(), 250);
      return;
    }

    const sectionsAreReady =
      uniqueSections.length > 0 &&
      uniqueSections.every((sectionId) => typeof cart.sections?.[sectionId] === 'string');

    if (sectionsAreReady) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'cart:discount',
            cart,
          },
        })
      );
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  } catch (error) {
    trigger.textContent = 'Try again';
    trigger.removeAttribute('tabindex');
    status?.classList.add('is-error');
    if (status) status.textContent = error.message || 'This discount could not be applied.';
  } finally {
    trigger.removeAttribute('aria-busy');
  }
}

function updateOfferCountdowns() {
  document.querySelectorAll(COUNTDOWN_SELECTOR).forEach((countdown) => {
    const value = countdown.querySelector('[data-countdown-value]');
    const rawEnd = (countdown.dataset.countdownEnd || '').trim();
    const hasExplicitTimeZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(rawEnd);
    const endTime = hasExplicitTimeZone ? Date.parse(rawEnd) : Number.NaN;

    if (!value || Number.isNaN(endTime)) {
      countdown.hidden = true;
      return;
    }

    const remaining = endTime - Date.now();

    if (remaining <= 0) {
      const offer = countdown.closest('.fg-product-offer, .fg-cart-offer');
      if (offer) offer.hidden = true;
      else countdown.hidden = true;
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const clock = [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');

    value.textContent = days > 0 ? `${days}d ${clock}` : clock;
    countdown.hidden = false;
  });
}

document.addEventListener('click', (event) => {
  const eventTarget = event.target instanceof Element ? event.target : event.target.parentElement;

  if (!eventTarget) return;

  const discountTrigger = eventTarget.closest(DISCOUNT_TRIGGER);

  if (discountTrigger) {
    event.preventDefault();
    applyCartDiscount(discountTrigger);
    return;
  }

  const copyTrigger = eventTarget.closest(COPY_TRIGGER);

  if (copyTrigger) {
    event.preventDefault();
    copyOfferCode(copyTrigger);
  }
});

if (document.querySelector(COUNTDOWN_SELECTOR)) {
  updateOfferCountdowns();
  const offerCountdownTimer = window.setInterval(updateOfferCountdowns, 1000);
  window.addEventListener('pagehide', () => window.clearInterval(offerCountdownTimer), { once: true });
}
